import { Head, Link } from "@inertiajs/react"
import { format } from "date-fns"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import {
  ArrowLeft,
  Edit,
  Trash2,
  Calendar,
  Target,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle,
} from "lucide-react"
import { AppLayout } from "@/layouts/app-layout"
import { goalsPath, editGoalPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "Goals",
    href: goalsPath(),
  },
  {
    title: "Goal Details",
    href: "#",
  },
]

interface GoalShowProps {
  goal: {
    id: number
    target_amount: number
    current_amount: number
    goal_type: string
    target_date: string | null
    created_at: string
    updated_at: string
    progress_percentage: number
    amount_remaining: number
    goal_type_human: string
    completed?: boolean
    months_to_completion?: number
  }
}

export default function GoalShow({ goal }: GoalShowProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMMM dd, yyyy")
  }

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), "MMM dd, yyyy 'at' h:mm a")
  }

  const getProgressColor = (progress: number) => {
    if (progress >= 100) return "text-green-600"
    if (progress >= 75) return "text-blue-600"
    if (progress >= 50) return "text-yellow-600"
    return "text-red-600"
  }

  const getStatusBadge = () => {
    if (goal.completed) {
      return (
        <Badge className="border-green-300 bg-green-100 text-green-800">
          <CheckCircle className="mr-1 h-3 w-3" />
          Completed
        </Badge>
      )
    }

    if (goal.progress_percentage >= 75) {
      return (
        <Badge className="border-blue-300 bg-blue-100 text-blue-800">
          <TrendingUp className="mr-1 h-3 w-3" />
          On Track
        </Badge>
      )
    }

    if (goal.progress_percentage >= 50) {
      return (
        <Badge className="border-yellow-300 bg-yellow-100 text-yellow-800">
          <Clock className="mr-1 h-3 w-3" />
          Progressing
        </Badge>
      )
    }

    return (
      <Badge className="border-red-300 bg-red-100 text-red-800">
        <AlertCircle className="mr-1 h-3 w-3" />
        Behind
      </Badge>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Goal Details" />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link href={goalsPath()}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Goals
              </Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Goal Details</h1>
              <p className="text-muted-foreground">
                Track your progress toward this financial goal
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild>
              <Link href={editGoalPath(goal.id)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit Goal
              </Link>
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (
                  confirm(
                    "Are you sure you want to delete this goal? This action cannot be undone.",
                  )
                ) {
                  // Handle delete - this would typically use Inertia's delete method
                  window.location.href = goalsPath()
                }
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>

        {/* Goal Overview */}
        <div className="grid gap-6 md:grid-cols-3">
          {/* Main Details */}
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  {goal.goal_type_human}
                </CardTitle>
                <div className="mt-2 flex items-center gap-2">
                  {getStatusBadge()}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Progress */}
                <div>
                  <div className="mb-4 flex justify-between text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <span
                      className={`font-medium ${getProgressColor(goal.progress_percentage)}`}
                    >
                      {goal.progress_percentage.toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={goal.progress_percentage} className="h-3" />
                </div>

                {/* Amounts */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="text-center">
                    <div className="text-muted-foreground mb-1 text-sm">
                      Current Amount
                    </div>
                    <div className="text-2xl font-bold">
                      {formatCurrency(goal.current_amount)}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-muted-foreground mb-1 text-sm">
                      Target Amount
                    </div>
                    <div className="text-2xl font-bold">
                      {formatCurrency(goal.target_amount)}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-muted-foreground mb-1 text-sm">
                      Remaining
                    </div>
                    <div className="text-2xl font-bold text-red-600">
                      {formatCurrency(goal.amount_remaining)}
                    </div>
                  </div>
                </div>

                {/* Target Date */}
                {goal.target_date && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="text-muted-foreground h-4 w-4" />
                      <span className="text-muted-foreground text-sm font-medium">
                        Target Date
                      </span>
                    </div>
                    <span className="text-sm">
                      {formatDate(goal.target_date)}
                    </span>
                  </div>
                )}

                {/* Time to Completion */}
                {goal.months_to_completion && goal.months_to_completion > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm font-medium">
                      Est. Completion Time
                    </span>
                    <span className="text-sm">
                      {goal.months_to_completion} months
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Metadata */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Goal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-muted-foreground mb-1 text-sm font-medium">
                    Goal Type
                  </div>
                  <div className="text-sm">{goal.goal_type_human}</div>
                </div>

                <div>
                  <div className="text-muted-foreground mb-1 text-sm font-medium">
                    Created
                  </div>
                  <div className="text-sm">
                    {formatDateTime(goal.created_at)}
                  </div>
                </div>

                <div>
                  <div className="text-muted-foreground mb-1 text-sm font-medium">
                    Last Updated
                  </div>
                  <div className="text-sm">
                    {formatDateTime(goal.updated_at)}
                  </div>
                </div>

                <div>
                  <div className="text-muted-foreground mb-1 text-sm font-medium">
                    Goal ID
                  </div>
                  <div className="font-mono text-sm">#{goal.id}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
